/**
 * Editing Office documents in Collabora Online (office.eurion.se).
 *
 * file-service hands out an edit session (the editor URL + a short-lived WOPI
 * token, see backend file-service routes/wopi.ts); the editor runs in an iframe
 * that we POST the token into, so it never appears in a URL.
 */
import { api } from './api';

/** Extensions Collabora can open. Edit vs. read-only is decided by file-service. */
const EDITABLE_EXTENSIONS = new Set([
  'doc', 'docx', 'docm', 'dot', 'dotx', 'odt', 'ott', 'rtf', 'txt',
  'xls', 'xlsx', 'xlsm', 'xlt', 'xltx', 'ods', 'ots', 'csv',
  'ppt', 'pptx', 'pptm', 'pot', 'potx', 'odp', 'otp',
]);

export function isEditableDocument(fileName: string): boolean {
  const ext = fileName.includes('.') ? fileName.split('.').pop()!.toLowerCase() : '';
  return EDITABLE_EXTENSIONS.has(ext);
}

export type NewDocumentType = 'document' | 'spreadsheet' | 'presentation';

export interface EditSession {
  actionUrl: string;
  accessToken: string;
  accessTokenTtl: number;
  canEdit: boolean;
  fileName: string;
}

export type EditSessionError = 'unavailable' | 'unsupported' | 'notFound' | 'generic';

/** Opens an editing session; `view` forces read-only. */
export async function openEditSession(fileId: string, mode: 'edit' | 'view' = 'edit'): Promise<EditSession> {
  const res = await api.post<EditSession>(`/v1/files/${encodeURIComponent(fileId)}/edit-session`, { mode });
  if (res.success && res.data) return res.data;
  throw new EditSessionFailure(errorFromCode(res.error?.code));
}

/** file-service's codes: SRV_002 editor not set up/reachable, VAL_003 type, RES_001 missing or no access. */
function errorFromCode(code?: string): EditSessionError {
  if (code === 'SRV_002') return 'unavailable';
  if (code === 'VAL_003') return 'unsupported';
  if (code === 'RES_001') return 'notFound';
  return 'generic';
}

export class EditSessionFailure extends Error {
  constructor(public readonly kind: EditSessionError) { super(kind); }
}

export interface CreatedFile {
  id: string;
  name: string;
  mime_type: string;
  size_bytes: number;
  room_id: string | null;
  created_at: string;
}

/** A blank .docx/.xlsx/.pptx, in the room when given (the caller must be a member). */
export async function createDocument(type: NewDocumentType, name: string, roomId?: string): Promise<CreatedFile> {
  const res = await api.post<CreatedFile>('/v1/files/new', { type, name: name.trim() || undefined, ...(roomId ? { roomId } : {}) });
  if (res.success && res.data) return res.data;
  throw new Error(res.error?.message ?? 'Could not create the file');
}

/** Collabora's UI language for an app language (it wants a region for some). */
export function editorLanguage(appLanguage: string | undefined): string {
  const lang = (appLanguage ?? 'en').toLowerCase().split('-')[0]!;
  const map: Record<string, string> = { en: 'en-GB', sv: 'sv', no: 'nb', nb: 'nb', da: 'da', de: 'de', fr: 'fr', nl: 'nl', pl: 'pl' };
  return map[lang] ?? 'en-GB';
}

/** The editor URL with its UI language. */
export function editorUrl(session: EditSession, appLanguage: string | undefined): string {
  const sep = session.actionUrl.includes('?') ? '&' : '?';
  return `${session.actionUrl}${sep}lang=${encodeURIComponent(editorLanguage(appLanguage))}`;
}
